import React from 'react';
import { LayoutDashboard, ClipboardList, Users, FileText, Building, Map, Target, GraduationCap, Grid3X3, BadgeCheck, BarChart3, Activity, Heart, Wrench, Briefcase, ShieldCheck, Scale, FolderCheck, Network, HeartPulse, Archive, DoorOpen, Gauge, UsersRound, Layers, UserRound, Megaphone } from 'lucide-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useApp } from '@/context/AppContext';
import ClientDashboard from '@/pages/client/ClientDashboard';
import ClientHealthSnapshot from '@/pages/client/ClientHealthSnapshot';
import ClientArchiveCenter from '@/pages/client/ClientArchiveCenter';
import ClientRequests from '@/pages/client/ClientRequests';
import ClientEmployees from '@/pages/client/ClientEmployees';
import ClientCommunications from '@/pages/client/ClientCommunications';
import ClientReports from '@/pages/client/ClientReports';
import ClientReportDetail from '@/pages/client/ClientReportDetail';
import ClientDepartments from '@/pages/client/ClientDepartments';
import ClientCompetencyConfig from '@/pages/client/ClientCompetencyConfig';
import ClientTalentMap from '@/pages/client/ClientTalentMap';
import ClientTalentCompetency from '@/pages/client/ClientTalentCompetency';
import ClientExpectedVsObserved from '@/pages/client/ClientExpectedVsObserved';
import ClientEvaluatorGrants from '@/pages/client/ClientEvaluatorGrants';
import ClientTalentTrain from '@/pages/client/ClientTalentTrain';
import ClientTalentCheck from '@/pages/client/ClientTalentCheck';
import ClientExternalCandidates from '@/pages/client/ClientExternalCandidates';
import ClientTalentStart from '@/pages/client/ClientTalentStart';
import ClientTalentStartDetail from '@/pages/client/ClientTalentStartDetail';
import ClientTalentHeart from '@/pages/client/ClientTalentHeart';
import ClientTalentFix from '@/pages/client/ClientTalentFix';
import ClientTalentFixContext from '@/pages/client/ClientTalentFixContext';
import ClientTalentFixConsent from '@/pages/client/ClientTalentFixConsent';
import ClientTalentExit from '@/pages/client/ClientTalentExit';
import ClientTalentPerformance from '@/pages/client/ClientTalentPerformance';
import ClientSuccession from '@/pages/client/ClientSuccession';
import ClientSuccessionPosition from '@/pages/client/ClientSuccessionPosition';
import ClientSuites from '@/pages/client/ClientSuites';
import ClientRecruitment from '@/pages/client/ClientRecruitment';
import ClientTalentIntelligence from '@/pages/client/ClientTalentIntelligence';
import ClientClimara from '@/pages/client/ClientClimara';
import ClientCompanyProfile from '@/pages/client/ClientCompanyProfile';
import ClientLibrary from '@/pages/client/ClientLibrary';
import ClientGlobalEthics from '@/pages/client/ClientGlobalEthics';
import ClientWorkforceRecords from '@/pages/client/ClientWorkforceRecords';
import AdminOrgBoard from '@/pages/admin/AdminOrgBoard';
import SuiteLocked from '@/pages/client/SuiteLocked';
import { useDecisionsBadge } from '@/lib/useDecisionsBadge';

// BUG 05/15 — Suite consoles in the Client portal that need plan-gating.
const CLIENT_SUITE_ITEMS = [
  { to: '/client/talentintelligence', label: 'TalentIntelligence',  icon: BarChart3,     suite: 'intelligence' },
  { to: '/client/climara',            label: 'Climara',             icon: Activity,      suite: 'climara' },
  { to: '/client/talentmap',          label: 'TalentMap',           icon: Map,           suite: 'talentmap' },
  { to: '/client/talentcheck',        label: 'TalentCheck',         icon: ShieldCheck,   suite: 'talentcheck' },
  { to: '/client/talentstart',        label: 'TalentStart',         icon: Briefcase,     suite: 'talentstart' },
  { to: '/client/talentheart',        label: 'TalentHeart',         icon: Heart,         suite: 'talentheart' },
  { to: '/client/talentfix',          label: 'TalentFix',           icon: Wrench,        suite: 'talentfix' },
  { to: '/client/talentcompetency',   label: 'TalentCompetency',    icon: Target,        suite: 'talentcompetency' },
  { to: '/client/talenttrain',        label: 'TalentTrain',         icon: GraduationCap, suite: 'talenttrain' },
  { to: '/client/workforce-records',  label: 'Workforce Records',   icon: FolderCheck,   suite: 'workforce_records' },
  { to: '/client/talentexit',         label: 'TalentExit',          icon: DoorOpen,      suite: 'talentexit' },
  { to: '/client/talentperformance',  label: 'TalentPerformance',   icon: Gauge,         suite: 'talentperformance' },
  { to: '/client/succession',         label: 'Succession',          icon: UsersRound,    suite: 'succession' },
];

const buildClientNav = (contractedSet, decisionsCount, isClientAdmin) => [
  { label: 'i18n:sidebar.organization', items: [
    { to: '/client', label: 'i18n:sidebar.dashboard', icon: LayoutDashboard, end: true },
    ...(isClientAdmin ? [{ to: '/client/health', label: 'i18n:sidebar.healthSnapshot', icon: HeartPulse }] : []),
    { to: '/client/company', label: 'i18n:sidebar.companyProfile', icon: Building },
    { to: '/client/departments', label: 'i18n:sidebar.departments', icon: Building },
    { to: '/client/org-board', label: 'i18n:sidebar.orgBoard', icon: Network },
    { to: '/client/employees', label: 'i18n:sidebar.collaborators', icon: Users },
    { to: '/client/communications', label: 'i18n:sidebar.communications', icon: Megaphone },
    // Founder UAT — personal context: the Client Admin is also a person and
    // can enter their OWN employee experience (TalentHeart profile, etc.).
    { to: '/me', label: 'i18n:sidebar.myPortal', icon: UserRound },
  ]},
  { label: 'i18n:sidebar.talent', items: [
    // Sesión F (ADD 3) — badge shows count of approved/rejected requests
    // since the client last opened the Requests page.
    {
      to: '/client/requests',
      label: 'i18n:sidebar.evaluations',
      icon: ClipboardList,
      badge: decisionsCount > 0 ? String(decisionsCount) : undefined,
    },
    { to: '/client/reports', label: 'i18n:sidebar.reports', icon: FileText },
    // Founder UAT 2026-06 — the Assessment Library is a core governed
    // resource, surfaced with the management functions (not after the suites).
    ...(isClientAdmin
      ? [{ to: '/client/library', label: 'i18n:sidebar.assessmentLibrary', icon: Layers }] : []),
    { to: '/client/recruitment', label: 'i18n:sidebar.recruitment', icon: BadgeCheck },
    // Sesión F (ADD 1) — full 9-suite catalog page (active + locked).
    { to: '/client/suites', label: 'i18n:sidebar.suites', icon: Grid3X3 },
    // Global Ethics — standalone module (client_admin only), NOT a suite.
    ...(isClientAdmin ? [{ to: '/client/global-ethics', label: 'Global Ethics', icon: Scale }] : []),
    // Archive Center — governed archived operational records (client_admin only).
    ...(isClientAdmin ? [{ to: '/client/archive', label: 'i18n:sidebar.archiveCenter', icon: Archive }] : []),
    // Competency Gaps (methodology: "Competencias: Esperado vs. Observado")
    // is injected right after TalentCompetency so the functional sequence
    // TalentCompetency → Competency Gaps → TalentTrain is visually clear.
    // Never surfaced to evaluated employees (Client Admin/authorized HR only).
    // Founder UAT 2026-06 — suites ordered alphabetically by visible name.
    // Competency Gaps stays injected right after TalentCompetency (documented
    // functional sequence TalentCompetency → Competency Gaps → TalentTrain).
    ...[...CLIENT_SUITE_ITEMS].sort((a, b) => a.label.localeCompare(b.label)).flatMap((it) => {
      const entry = { ...it, locked: !contractedSet.has(it.suite) };
      if (it.suite === 'talentcompetency' && isClientAdmin && contractedSet.has('talentcompetency')) {
        return [entry, { to: '/client/talentcompetency/evo', label: 'i18n:sidebar.competencyGaps', icon: Layers }];
      }
      return [entry];
    }),
  ]},
];

const Gated = ({ suite, name, code, contractedSet, children }) => (
  contractedSet.has(suite) ? children : <SuiteLocked name={name} code={code} />
);

export default function ClientRoutes() {
  const { user } = useApp();
  const contractedSet = React.useMemo(
    () => new Set(user?.company_suite_codes || []),
    [user?.company_suite_codes],
  );
  const decisionsCount = useDecisionsBadge();
  const CLIENT_NAV = React.useMemo(
    () => buildClientNav(contractedSet, decisionsCount, user?.role === 'client_admin'),
    [contractedSet, decisionsCount, user?.role],
  );

  return (
    <Routes>
      <Route element={<DashboardLayout nav={CLIENT_NAV} portalLabel="Client Portal" />}>
        <Route index element={<ClientDashboard />} />
        <Route path="company" element={<ClientCompanyProfile />} />
        <Route path="requests" element={<ClientRequests />} />
        <Route path="employees" element={<ClientEmployees />} />
        <Route path="communications" element={<ClientCommunications />} />
        <Route path="reports" element={<ClientReports />} />
        <Route path="reports/:id" element={<ClientReportDetail />} />
        <Route path="departments" element={<ClientDepartments />} />
        <Route path="org-board" element={<AdminOrgBoard clientMode />} />
        <Route path="suites" element={<ClientSuites />} />
        <Route path="library" element={<ClientLibrary />} />
        <Route path="global-ethics" element={user?.role === 'client_admin' ? <ClientGlobalEthics /> : <Navigate to="/client" replace />} />
        <Route path="health" element={user?.role === 'client_admin' ? <ClientHealthSnapshot /> : <Navigate to="/client" replace />} />
        <Route path="archive" element={user?.role === 'client_admin' ? <ClientArchiveCenter /> : <Navigate to="/client" replace />} />
        <Route path="workforce-records" element={
          <Gated suite="workforce_records" name="Workforce Records" code="WR" contractedSet={contractedSet}>
            <ClientWorkforceRecords />
          </Gated>
        } />
        <Route path="talentexit" element={
          <Gated suite="talentexit" name="TalentExit" code="TX" contractedSet={contractedSet}>
            <ClientTalentExit />
          </Gated>
        } />
        <Route path="talentperformance" element={
          <Gated suite="talentperformance" name="TalentPerformance" code="TP" contractedSet={contractedSet}>
            <ClientTalentPerformance />
          </Gated>
        } />
        <Route path="succession" element={
          <Gated suite="succession" name="Succession" code="SC" contractedSet={contractedSet}>
            <ClientSuccession />
          </Gated>
        } />
        <Route path="succession/:id" element={
          <Gated suite="succession" name="Succession" code="SC" contractedSet={contractedSet}>
            <ClientSuccessionPosition />
          </Gated>
        } />
        <Route path="recruitment" element={<ClientRecruitment />} />
        <Route path="recruitment/candidates" element={<ClientExternalCandidates />} />
        <Route path="recruitment/:id" element={<ClientRecruitment />} />
        <Route path="talentintelligence" element={
          <Gated suite="intelligence" name="TalentIntelligence" code="TI" contractedSet={contractedSet}>
            <ClientTalentIntelligence />
          </Gated>
        } />
        <Route path="climara" element={
          <Gated suite="climara" name="Climara" code="CL" contractedSet={contractedSet}>
            <ClientClimara />
          </Gated>
        } />
        <Route path="talentmap" element={
          <Gated suite="talentmap" name="TalentMap" code="TM" contractedSet={contractedSet}>
            <ClientTalentMap />
          </Gated>
        } />
        <Route path="talentcheck" element={
          <Gated suite="talentcheck" name="TalentCheck" code="TCK" contractedSet={contractedSet}>
            <ClientTalentCheck />
          </Gated>
        } />
        <Route path="talentstart" element={
          <Gated suite="talentstart" name="TalentStart" code="TS" contractedSet={contractedSet}>
            <ClientTalentStart />
          </Gated>
        } />
        <Route path="talentstart/:assignmentId" element={
          <Gated suite="talentstart" name="TalentStart" code="TS" contractedSet={contractedSet}>
            <ClientTalentStartDetail />
          </Gated>
        } />
        <Route path="talentheart" element={
          <Gated suite="talentheart" name="TalentHeart" code="TH" contractedSet={contractedSet}>
            <ClientTalentHeart />
          </Gated>
        } />
        <Route path="talentfix" element={
          <Gated suite="talentfix" name="TalentFix" code="TFX" contractedSet={contractedSet}>
            <ClientTalentFix />
          </Gated>
        } />
        <Route path="talentfix/cases/:id/context" element={
          <Gated suite="talentfix" name="TalentFix" code="TFX" contractedSet={contractedSet}>
            <ClientTalentFixContext />
          </Gated>
        } />
        <Route path="talentfix/cases/:id/consent" element={
          <Gated suite="talentfix" name="TalentFix" code="TFX" contractedSet={contractedSet}>
            <ClientTalentFixConsent />
          </Gated>
        } />
        <Route path="talentcompetency/evo" element={
          <Gated suite="talentcompetency" name="TalentCompetency" code="TC" contractedSet={contractedSet}>
            {user?.role === 'client_admin' ? <ClientExpectedVsObserved /> : <Navigate to="/client" replace />}
          </Gated>
        } />
        <Route path="talentcompetency/evaluadores" element={
          <Gated suite="talentcompetency" name="TalentCompetency" code="TC" contractedSet={contractedSet}>
            {user?.role === 'client_admin' ? <ClientEvaluatorGrants /> : <Navigate to="/client" replace />}
          </Gated>
        } />
        <Route path="talentcompetency/competencias" element={
          <Gated suite="talentcompetency" name="TalentCompetency" code="TC" contractedSet={contractedSet}>
            {user?.role === 'client_admin' ? <ClientCompetencyConfig /> : <Navigate to="/client" replace />}
          </Gated>
        } />
        <Route path="talentcompetency" element={
          <Gated suite="talentcompetency" name="TalentCompetency" code="TC" contractedSet={contractedSet}>
            <ClientTalentCompetency />
          </Gated>
        } />
        <Route path="talenttrain" element={
          <Gated suite="talenttrain" name="TalentTrain" code="TT" contractedSet={contractedSet}>
            <ClientTalentTrain />
          </Gated>
        } />
        <Route path="talenttrain/:userId" element={
          <Gated suite="talenttrain" name="TalentTrain" code="TT" contractedSet={contractedSet}>
            <ClientTalentTrain />
          </Gated>
        } />
        <Route path="*" element={<Navigate to="/client" replace />} />
      </Route>
    </Routes>
  );
}
